import type {
  Item, Outfit, CartLine, User, Order, FilterState, ChatMessage, Category
} from './types';
import {
  SVG, COLOR_HEX, TAGS, ITEMS, ITEM_BY_ID, OUTFITS, colorScore
} from './data';

// ========== Storage ==========
const Store = {
  load<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  save(key: string, v: unknown): void {
    localStorage.setItem(key, JSON.stringify(v));
  },
  remove(key: string): void {
    localStorage.removeItem(key);
  },
};

interface AppState {
  user: User | null;
  demoMode: boolean;
  cart: CartLine[];
  orders: Order[];
  likes: Set<string>;
  wardrobe: Item[];
  filters: FilterState;
  chat: ChatMessage[];
}

const State: AppState = {
  user: null,
  demoMode: false,
  cart: [],
  orders: [],
  likes: new Set<string>(Store.load<string[]>('likes', [])),
  wardrobe: Store.load<Item[]>('wardrobe', []),
  filters: { occasion: null, season: null, style: null },
  chat: Store.load<ChatMessage[]>('chat', []),
};

const persistLikes    = () => Store.save('likes', [...State.likes]);
const persistWardrobe = () => Store.save('wardrobe', State.wardrobe);
const persistChat     = () => Store.save('chat', State.chat);
const persistDemoCart = () => Store.save('demoCart', State.cart);

// ========== API ==========
interface ApiOpts {
  method?: string;
  body?: unknown;
}

async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method || 'GET',
    credentials: 'same-origin',
    headers: opts.body ? { 'content-type': 'application/json' } : {},
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; }
  catch { throw new Error('返回数据无效'); }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data as T;
}

// ========== DOM helpers ==========
const $  = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);
const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  [...root.querySelectorAll<T>(sel)];

type Attrs = Record<string, unknown>;
type Child = Node | string | null | false | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') e.className = String(v);
    else if (k === 'html') e.innerHTML = String(v);
    else if (k.startsWith('on') && typeof v === 'function') {
      e.addEventListener(k.slice(2), v as EventListener);
    } else {
      e.setAttribute(k, String(v));
    }
  }
  for (const c of children.flat() as Child[]) {
    if (c == null || c === false) continue;
    e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return e;
}

interface ChipOpts {
  active?: boolean;
  accent?: boolean;
  onclick?: (e: MouseEvent) => void;
}

function chip(text: string, opts: ChipOpts = {}): HTMLButtonElement {
  const cls = ['chip'];
  if (opts.active) cls.push('chip-active');
  if (opts.accent) cls.push('chip-accent');
  return el('button', { class: cls.join(' '), onclick: opts.onclick }, text);
}

function getItem(id: string): Item | undefined {
  return ITEM_BY_ID[id] || State.wardrobe.find(w => w.id === id);
}

const totalCartCount = () => State.cart.reduce((s, c) => s + c.qty, 0);
const totalCartPrice = () =>
  State.cart.reduce((s, c) => s + ((getItem(c.item_id)?.price || 0) * c.qty), 0);

// ========== Routing ==========
type Route = 'feed' | 'match' | 'wardrobe' | 'stylist' | 'shop' | 'cart';
const Routes: Route[] = ['feed', 'match', 'wardrobe', 'stylist', 'shop', 'cart'];

function go(route: Route): void {
  window.location.hash = '#' + route;
}

function currentRoute(): Route {
  const h = window.location.hash.slice(1) as Route;
  return Routes.includes(h) ? h : 'feed';
}

window.addEventListener('hashchange', render);
window.addEventListener('DOMContentLoaded', bootstrap);

async function bootstrap(): Promise<void> {
  $$<HTMLElement>('[data-route]').forEach(b =>
    b.addEventListener('click', () => go(b.dataset.route as Route))
  );
  $('#accountBtn')?.addEventListener('click', openAccount);

  try {
    const me = await api<{ user: User | null }>('/api/auth/me');
    State.user = me.user;
    if (State.user) {
      try {
        const c = await api<{ items: CartLine[] }>('/api/cart');
        State.cart = c.items || [];
      } catch { /* 忽略 */ }
    }
  } catch {
    State.demoMode = true;
    State.cart = Store.load<CartLine[]>('demoCart', []);
    $('#demoBanner')?.classList.remove('hidden');
  }

  // 重建衣橱中持久化时丢失的 render 函数
  State.wardrobe = State.wardrobe.map(w => ({ ...w, render: w.render || makeRenderFor(w.cat, w.color) }));

  render();
}

const Views: Record<Route, (root: HTMLElement) => void> = {
  feed: renderFeed,
  match: renderMatch,
  wardrobe: renderWardrobe,
  stylist: renderStylist,
  shop: renderShop,
  cart: renderCart,
};

function render(): void {
  const r = currentRoute();
  $$<HTMLElement>('[data-route]').forEach(b =>
    b.classList.toggle('active', b.dataset.route === r)
  );
  refreshHeader();
  const main = $<HTMLElement>('#app');
  if (!main) return;
  main.innerHTML = '';
  Views[r](main);
}

function refreshHeader(): void {
  const accountBtn = $<HTMLButtonElement>('#accountBtn');
  if (accountBtn) {
    if (State.user) {
      accountBtn.textContent = State.user.name || State.user.email.split('@')[0]!;
      accountBtn.classList.add('bg-ink-100');
    } else {
      accountBtn.textContent = '登录';
      accountBtn.classList.remove('bg-ink-100');
    }
  }
  const badge = $<HTMLElement>('#cartBadge');
  if (badge) {
    const n = totalCartCount();
    if (n > 0) {
      badge.classList.remove('hidden');
      badge.textContent = n > 99 ? '99+' : String(n);
    } else {
      badge.classList.add('hidden');
    }
  }
}

// ========== Toast ==========
function toast(text: string, kind: 'info' | 'error' = 'info'): void {
  const t = el('div', {
    class: 'fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-full text-sm shadow-lg ' +
      (kind === 'error' ? 'bg-accent text-white' : 'bg-ink-900 text-ink-50')
  }, text);
  document.body.appendChild(t);
  setTimeout(() => { t.style.transition = 'opacity .3s'; t.style.opacity = '0'; }, 1800);
  setTimeout(() => t.remove(), 2200);
}

// ========== Cart ops ==========
async function addToCart(itemId: string, qty: number = 1): Promise<void> {
  if (State.demoMode || !State.user) {
    if (!State.demoMode) { toast('请先登录'); openAccount(); return; }
    const exist = State.cart.find(c => c.item_id === itemId);
    if (exist) exist.qty = Math.min(99, exist.qty + qty);
    else State.cart.unshift({ item_id: itemId, qty, added_at: Date.now() });
    persistDemoCart();
    refreshHeader();
    toast('已加入购物车');
    return;
  }
  try {
    const r = await api<{ items: CartLine[] }>('/api/cart', { method: 'POST', body: { item_id: itemId, qty } });
    State.cart = r.items;
    refreshHeader();
    toast('已加入购物车');
  } catch (e) {
    toast((e as Error).message, 'error');
  }
}

async function setCartQty(itemId: string, qty: number): Promise<void> {
  if (State.demoMode || !State.user) {
    if (qty <= 0) State.cart = State.cart.filter(c => c.item_id !== itemId);
    else {
      const x = State.cart.find(c => c.item_id === itemId);
      if (x) x.qty = qty;
    }
    persistDemoCart();
    render();
    return;
  }
  try {
    const r = await api<{ items: CartLine[] }>('/api/cart/' + encodeURIComponent(itemId), {
      method: 'PATCH', body: { qty }
    });
    State.cart = r.items;
    render();
  } catch (e) {
    toast((e as Error).message, 'error');
  }
}

async function removeFromCart(itemId: string): Promise<void> {
  if (State.demoMode || !State.user) {
    State.cart = State.cart.filter(c => c.item_id !== itemId);
    persistDemoCart();
    render();
    return;
  }
  try {
    const r = await api<{ items: CartLine[] }>('/api/cart/' + encodeURIComponent(itemId), { method: 'DELETE' });
    State.cart = r.items;
    render();
  } catch (e) {
    toast((e as Error).message, 'error');
  }
}

async function checkout(): Promise<void> {
  if (State.cart.length === 0) return;
  if (State.demoMode || !State.user) {
    if (!State.user) { toast('请先登录再结账'); openAccount(); return; }
    State.cart = [];
    persistDemoCart();
    render();
    toast('订单已创建(演示)');
    return;
  }
  const items = State.cart.map(c => {
    const it = getItem(c.item_id);
    return { id: c.item_id, name: it?.name || c.item_id, price: it?.price || 0, qty: c.qty };
  });
  try {
    const r = await api<{ order: Order }>('/api/checkout', { method: 'POST', body: { items } });
    State.cart = [];
    render();
    toast(`订单 #${r.order.id} 已创建,共 ¥${r.order.total}`);
  } catch (e) {
    toast((e as Error).message, 'error');
  }
}

// ========== 灵感流 ==========
function renderFeed(root: HTMLElement): void {
  root.appendChild(el('section', { class: 'mb-8' },
    el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-3' }, '今天,要怎么穿?'),
    el('p',  { class: 'text-ink-800/65 max-w-xl' }, '为你精选的 12 套日常灵感。点开任意一套,可一键查看单品并搜索同款。')
  ));

  const filterBar = el('div', { class: 'mb-6 flex flex-wrap gap-2 items-center' });
  filterBar.appendChild(el('span', { class: 'text-xs text-ink-800/55 mr-1' }, '场合'));
  ['全部', ...TAGS.occasion].forEach(o => {
    const v = o === '全部' ? null : o;
    filterBar.appendChild(chip(o, {
      active: State.filters.occasion === v,
      onclick: () => { State.filters.occasion = v as FilterState['occasion']; render(); },
    }));
  });
  filterBar.appendChild(el('span', { class: 'text-xs text-ink-800/55 ml-3 mr-1' }, '季节'));
  ['全部', ...TAGS.season].forEach(s => {
    const v = s === '全部' ? null : s;
    filterBar.appendChild(chip(s, {
      active: State.filters.season === v,
      onclick: () => { State.filters.season = v as FilterState['season']; render(); },
    }));
  });
  filterBar.appendChild(el('span', { class: 'text-xs text-ink-800/55 ml-3 mr-1' }, '风格'));
  ['全部', ...TAGS.style].forEach(s => {
    const v = s === '全部' ? null : s;
    filterBar.appendChild(chip(s, {
      active: State.filters.style === v,
      onclick: () => { State.filters.style = v as FilterState['style']; render(); },
    }));
  });
  root.appendChild(filterBar);

  const filtered = OUTFITS.filter(o =>
    (!State.filters.occasion || o.occasion === State.filters.occasion) &&
    (!State.filters.season   || o.season   === State.filters.season)   &&
    (!State.filters.style    || o.style    === State.filters.style)
  );

  if (filtered.length === 0) {
    root.appendChild(el('div', { class: 'empty' }, '没有匹配的搭配,试试别的筛选。'));
    return;
  }

  const grid = el('div', { class: 'masonry' });
  filtered.forEach(o => grid.appendChild(outfitCard(o)));
  root.appendChild(grid);
}

function outfitCard(outfit: Outfit): HTMLElement {
  const liked = State.likes.has(outfit.id);
  return el('article', { class: 'outfit-card', onclick: () => openOutfit(outfit) },
    el('div', { class: 'p-4 grid grid-cols-2 gap-2 bg-ink-100' },
      ...outfit.pieces.slice(0, 4).map(pid => {
        const it = getItem(pid);
        return el('div', { class: 'aspect-square rounded-lg bg-white p-1', html: it ? it.render() : '' });
      })
    ),
    el('div', { class: 'p-4' },
      el('div', { class: 'flex items-start justify-between gap-2' },
        el('h3', { class: 'font-display text-lg font-semibold' }, outfit.title),
        el('button', {
          class: 'text-xl shrink-0 ' + (liked ? 'text-accent liked' : 'text-ink-200 hover:text-accent'),
          onclick: (e: MouseEvent) => { e.stopPropagation(); toggleLike(outfit.id); render(); },
        }, liked ? '♥' : '♡')
      ),
      el('p', { class: 'text-sm text-ink-800/65 mt-1.5 leading-relaxed' }, outfit.desc),
      el('div', { class: 'flex flex-wrap gap-1.5 mt-3' },
        chip(outfit.occasion), chip(outfit.season), chip(outfit.style, { accent: true })
      )
    )
  );
}

function toggleLike(id: string): void {
  if (State.likes.has(id)) State.likes.delete(id); else State.likes.add(id);
  persistLikes();
}

// ========== 搭配详情 ==========
function openOutfit(outfit: Outfit): void {
  const root = $<HTMLElement>('#modalRoot');
  if (!root) return;
  const close = () => { root.innerHTML = ''; };
  const liked = State.likes.has(outfit.id);

  const modal = el('div', {
    class: 'modal-bg',
    onclick: (e: MouseEvent) => { if ((e.target as HTMLElement).classList.contains('modal-bg')) close(); }
  },
    el('div', { class: 'modal-panel p-6 md:p-10' },
      el('div', { class: 'flex items-start justify-between gap-4 mb-4' },
        el('div', {},
          el('div', { class: 'flex flex-wrap gap-1.5 mb-2' },
            chip(outfit.occasion), chip(outfit.season), chip(outfit.style, { accent: true })
          ),
          el('h2', { class: 'font-display text-3xl md:text-4xl font-semibold mb-2' }, outfit.title),
          el('p',  { class: 'text-ink-800/70 max-w-xl' }, outfit.desc)
        ),
        el('button', { class: 'text-2xl text-ink-800/50 hover:text-ink-900', onclick: close }, '×')
      ),

      el('div', { class: 'flex gap-3 mb-6 flex-wrap' },
        el('button', {
          class: 'px-5 py-2.5 rounded-full text-sm font-medium ' +
                 (liked ? 'bg-accent text-white' : 'bg-ink-100 hover:bg-ink-200'),
          onclick: () => { toggleLike(outfit.id); close(); render(); }
        }, liked ? '♥ 已收藏' : '♡ 收藏这套'),
        el('button', {
          class: 'px-5 py-2.5 rounded-full text-sm font-medium bg-ink-900 text-ink-50 hover:bg-ink-800',
          onclick: async () => {
            for (const pid of outfit.pieces) {
              const it = getItem(pid);
              if (it && it.price) await addToCart(pid, 1);
            }
            close();
            go('cart');
          }
        }, '一键凑同款 → 加入购物车')
      ),

      el('h3', { class: 'font-display text-xl font-semibold mb-4' }, '这套包含 ' + outfit.pieces.length + ' 件单品'),
      el('div', { class: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4' },
        ...outfit.pieces.map(pid => {
          const it = getItem(pid);
          if (!it) return null;
          return el('div', { class: 'rounded-2xl border border-ink-200 bg-white overflow-hidden' },
            el('div', { class: 'aspect-square bg-ink-100 p-3', html: it.render() }),
            el('div', { class: 'p-3' },
              el('div', { class: 'text-xs text-ink-800/55' }, it.cat),
              el('div', { class: 'text-sm font-medium mt-0.5' }, it.name),
              it.price ? el('div', { class: 'text-sm text-accent-deep mt-1' }, '¥' + it.price) : null,
              it.price ? el('button', {
                class: 'mt-2 text-xs px-2.5 py-1 rounded-full bg-ink-100 hover:bg-ink-200',
                onclick: (e: MouseEvent) => { e.stopPropagation(); addToCart(pid, 1); }
              }, '加入购物车') : null
            )
          );
        })
      )
    )
  );
  root.appendChild(modal);
}

// ========== 单品搭配 ==========
function renderMatch(root: HTMLElement): void {
  root.appendChild(el('section', { class: 'mb-8' },
    el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-3' }, '从一件单品开始'),
    el('p',  { class: 'text-ink-800/65 max-w-xl' }, '选一件你的单品(衣橱里的或商城里的),我会帮你搭出 3 套完整造型。')
  ));

  const seedId = Store.load<string | null>('match_seed', null);
  const seed = seedId ? getItem(seedId) : undefined;

  root.appendChild(el('h2', { class: 'font-display text-xl font-semibold mb-3' }, seed ? '当前单品' : '先选一件'));

  if (seed) {
    root.appendChild(el('div', { class: 'mb-8 flex items-center gap-4 p-4 rounded-2xl border border-ink-200 bg-white' },
      el('div', { class: 'w-24 h-24 bg-ink-100 rounded-xl p-2', html: seed.render() }),
      el('div', { class: 'flex-1' },
        el('div', { class: 'text-xs text-ink-800/55' }, seed.cat + ' · ' + seed.color),
        el('div', { class: 'font-medium' }, seed.name),
        el('div', { class: 'text-xs text-ink-800/55 mt-1' }, (seed.style || []).join(' / '))
      ),
      el('button', {
        class: 'px-3 py-1.5 text-xs rounded-full bg-ink-100 hover:bg-ink-200',
        onclick: () => { Store.save('match_seed', null); render(); }
      }, '换一件')
    ));

    const suggestions = generateOutfitsFromSeed(seed, 3);
    root.appendChild(el('h2', { class: 'font-display text-xl font-semibold mb-4' }, '为你生成的 ' + suggestions.length + ' 套搭配'));

    if (suggestions.length === 0) {
      root.appendChild(el('div', { class: 'empty' }, '暂时找不到合适的搭配。试试给单品打上风格/季节标签,或换一件再试。'));
    } else {
      const grid = el('div', { class: 'grid md:grid-cols-3 gap-5' });
      suggestions.forEach((outfit, i) => grid.appendChild(generatedOutfitCard(outfit, seed, i)));
      root.appendChild(grid);
    }
  } else {
    const pool = [...State.wardrobe, ...ITEMS];
    if (pool.length === 0) {
      root.appendChild(el('div', { class: 'empty' }, '还没有可选单品。先去衣橱上传一件,或浏览商城。'));
      return;
    }
    const grid = el('div', { class: 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3' });
    pool.forEach(it => {
      grid.appendChild(el('button', {
        class: 'rounded-xl border border-ink-200 bg-white hover:border-accent transition p-2',
        onclick: () => { Store.save('match_seed', it.id); render(); }
      },
        el('div', { class: 'aspect-square bg-ink-100 rounded-lg p-1 mb-1.5', html: it.render() }),
        el('div', { class: 'text-xs truncate' }, it.name)
      ));
    });
    root.appendChild(grid);
  }
}

function generatedOutfitCard(outfit: Outfit, seed: Item, _idx: number): HTMLElement {
  return el('article', { class: 'outfit-card', onclick: () => openOutfit(outfit) },
    el('div', { class: 'p-4 grid grid-cols-2 gap-2 bg-ink-100' },
      ...outfit.pieces.slice(0, 4).map(pid => {
        const it = getItem(pid);
        return el('div', {
          class: 'aspect-square rounded-lg bg-white p-1 ' + (pid === seed.id ? 'ring-2 ring-accent' : ''),
          html: it ? it.render() : '',
        });
      })
    ),
    el('div', { class: 'p-4' },
      el('div', { class: 'text-xs text-ink-800/55 mb-1' }, '基于 ' + seed.name),
      el('h3', { class: 'font-display text-lg font-semibold' }, outfit.title),
      el('p',  { class: 'text-sm text-ink-800/65 mt-1.5 leading-relaxed' }, outfit.desc),
      el('div', { class: 'flex flex-wrap gap-1.5 mt-3' },
        chip(outfit.occasion), chip(outfit.season), chip(outfit.style, { accent: true })
      )
    )
  );
}

function generateOutfitsFromSeed(seed: Item, n = 3): Outfit[] {
  const want: Category[] = seed.cat === '连衣裙'
    ? ['连衣裙', '鞋履', '包袋', '外套']
    : ['上衣', '下装', '外套', '鞋履', '包袋'];
  const seedStyles  = seed.style   || [];
  const seedSeasons = seed.season  || [];
  const seedOccs    = seed.occasion || [];
  const pool: Item[] = [...ITEMS, ...State.wardrobe];
  const outfits: Outfit[] = [];
  const styleTries = seedStyles.length ? seedStyles : ['极简'];

  styleTries.slice(0, n).forEach((style, idx) => {
    const pieces: string[] = [seed.id];
    const usedIds = new Set<string>([seed.id]);
    want.forEach(cat => {
      if (cat === seed.cat) return;
      const candidates = pool
        .filter(it => it.cat === cat && !usedIds.has(it.id))
        .map(it => {
          let s = 0;
          if (it.style?.includes(style))                     s += 2;
          if (it.season?.some(x => seedSeasons.includes(x))) s += 1;
          if (it.occasion?.some(x => seedOccs.includes(x)))  s += 1;
          s += colorScore(it.color, seed.color);
          s += Math.random() * 0.5;
          return { it, s };
        })
        .sort((a, b) => b.s - a.s);
      const pick = candidates[idx % Math.max(candidates.length, 1)];
      if (pick && pick.s > 1.0) { pieces.push(pick.it.id); usedIds.add(pick.it.id); }
    });

    if (pieces.length >= 3) {
      outfits.push({
        id: 'gen-' + seed.id + '-' + idx,
        title: ['极简之选', '日常通勤', '气质升级', '轻松出街', '约会方案'][idx] || ('搭配 ' + (idx + 1)),
        desc: descForOutfit(pieces, style),
        pieces, style,
        season: seedSeasons[0] || '春',
        occasion: seedOccs[0] || '休闲',
      });
    }
  });
  return outfits;
}

function descForOutfit(pieces: string[], style: string): string {
  const names = pieces.map(id => getItem(id)?.name).filter(Boolean) as string[];
  return names.slice(0, 3).join(' + ') + ',' + style + '风格的清爽造型。';
}

// ========== 衣橱 ==========
function renderWardrobe(root: HTMLElement): void {
  root.appendChild(el('section', { class: 'mb-8 flex items-end justify-between flex-wrap gap-4' },
    el('div', {},
      el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-3' }, '我的衣橱'),
      el('p',  { class: 'text-ink-800/65' }, '上传你的真实衣物,系统会为你生成专属搭配。已收藏 ' + State.likes.size + ' 套灵感。')
    ),
    el('button', {
      class: 'px-5 py-2.5 rounded-full bg-ink-900 text-ink-50 hover:bg-ink-800 text-sm font-medium',
      onclick: openUpload
    }, '+ 添加衣物')
  ));

  if (State.wardrobe.length === 0) {
    root.appendChild(el('div', { class: 'empty' },
      el('div', { class: 'text-2xl mb-2' }, '👕'),
      el('div', { class: 'mb-3' }, '衣橱是空的'),
      el('button', {
        class: 'px-5 py-2.5 rounded-full bg-ink-900 text-ink-50 text-sm',
        onclick: openUpload
      }, '上传第一件衣物')
    ));
    return;
  }

  const groups: Record<string, Item[]> = {};
  State.wardrobe.forEach(w => { (groups[w.cat] ||= []).push(w); });

  Object.entries(groups).forEach(([cat, items]) => {
    root.appendChild(el('h2', { class: 'font-display text-xl font-semibold mt-8 mb-4' }, cat + ' · ' + items.length));
    const grid = el('div', { class: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4' });
    items.forEach(it => {
      grid.appendChild(el('div', { class: 'rounded-2xl border border-ink-200 bg-white overflow-hidden group' },
        el('div', { class: 'aspect-square bg-ink-100 relative' },
          it.image
            ? el('img', { src: it.image, class: 'w-full h-full object-cover' })
            : el('div', { class: 'p-3 w-full h-full', html: it.render() }),
          el('button', {
            class: 'absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 text-sm hidden group-hover:flex items-center justify-center hover:bg-white',
            onclick: () => removeWardrobe(it.id)
          }, '×')
        ),
        el('div', { class: 'p-3' },
          el('div', { class: 'text-sm font-medium' }, it.name),
          el('div', { class: 'text-xs text-ink-800/55 mt-0.5' }, it.color + ' · ' + (it.style || []).slice(0, 2).join('/')),
          el('button', {
            class: 'mt-2 text-xs text-accent hover:underline',
            onclick: () => { Store.save('match_seed', it.id); go('match'); }
          }, '为它搭配 →')
        )
      ));
    });
    root.appendChild(grid);
  });
}

function removeWardrobe(id: string): void {
  if (!confirm('从衣橱中移除这件衣物?')) return;
  State.wardrobe = State.wardrobe.filter(w => w.id !== id);
  persistWardrobe();
  render();
}

function makeRenderFor(cat: string, color: string): () => string {
  const map: Record<string, string> = {
    '上衣': 'top', '下装': 'bottom', '连衣裙': 'dress', '外套': 'outerwear',
    '鞋履': 'shoes', '包袋': 'bag', '配饰': 'accessory', '帽子': 'hat',
  };
  const key = map[cat] || 'top';
  return () => SVG[key]!(COLOR_HEX[color] || '#999');
}

function openUpload(): void {
  const root = $<HTMLElement>('#modalRoot');
  if (!root) return;
  const close = () => { root.innerHTML = ''; };

  let imageData: string | null = null;
  const chosenStyles = new Set<string>();
  const chosenSeasons = new Set<string>();
  const chosenOccasions = new Set<string>();

  const previewBox = el('div', {
    class: 'aspect-square bg-ink-100 rounded-xl flex items-center justify-center text-ink-800/40 text-sm'
  }, '点击选择图片(可选)');
  const fileInput = el('input', {
    type: 'file', accept: 'image/*', class: 'hidden',
    onchange: (e: Event) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        imageData = ev.target?.result as string;
        previewBox.innerHTML = '';
        previewBox.appendChild(el('img', { src: imageData, class: 'w-full h-full object-cover rounded-xl' }));
      };
      reader.readAsDataURL(f);
    },
  });
  previewBox.style.cursor = 'pointer';
  previewBox.onclick = () => fileInput.click();

  const nameInput = el('input', {
    class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm',
    placeholder: '比如:我的 Levi\'s 直筒裤'
  });
  const catSelect = el('select', { class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm' },
    ...TAGS.category.map(c => el('option', { value: c }, c)));
  const colorSelect = el('select', { class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm' },
    ...TAGS.color.map(c => el('option', { value: c }, c)));

  function chipGroup(tags: readonly string[], set: Set<string>): HTMLElement {
    return el('div', { class: 'flex flex-wrap gap-1.5' },
      ...tags.map(t => {
        const c = chip(t, {
          active: set.has(t),
          onclick: () => {
            if (set.has(t)) set.delete(t); else set.add(t);
            c.classList.toggle('chip-active');
          },
        });
        return c;
      })
    );
  }

  const modal = el('div', {
    class: 'modal-bg',
    onclick: (e: MouseEvent) => { if ((e.target as HTMLElement).classList.contains('modal-bg')) close(); }
  },
    el('div', { class: 'modal-panel p-6 md:p-8 max-w-2xl' },
      el('div', { class: 'flex items-start justify-between mb-6' },
        el('h2', { class: 'font-display text-2xl font-semibold' }, '添加新衣物'),
        el('button', { class: 'text-2xl text-ink-800/50', onclick: close }, '×')
      ),
      el('div', { class: 'grid md:grid-cols-2 gap-6' },
        el('div', {}, previewBox, fileInput),
        el('div', { class: 'space-y-4' },
          el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '名称'), nameInput),
          el('div', { class: 'grid grid-cols-2 gap-3' },
            el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '类别'), catSelect),
            el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '主色'), colorSelect)
          ),
          el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '风格(可多选)'), chipGroup(TAGS.style, chosenStyles)),
          el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '季节(可多选)'), chipGroup(TAGS.season, chosenSeasons)),
          el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '场合(可多选)'), chipGroup(TAGS.occasion, chosenOccasions))
        )
      ),
      el('div', { class: 'flex justify-end gap-3 mt-6 pt-6 border-t border-ink-200' },
        el('button', { class: 'px-5 py-2.5 rounded-full text-sm bg-ink-100 hover:bg-ink-200', onclick: close }, '取消'),
        el('button', {
          class: 'px-5 py-2.5 rounded-full text-sm bg-ink-900 text-ink-50 hover:bg-ink-800',
          onclick: () => {
            const cat = catSelect.value as Category;
            const color = colorSelect.value;
            const item: Item = {
              id: 'user-' + Date.now(),
              name: nameInput.value.trim() || (color + cat),
              cat, color,
              style: [...chosenStyles],
              season: [...chosenSeasons],
              occasion: [...chosenOccasions],
              price: 0,
              ...(imageData ? { image: imageData } : {}),
              render: makeRenderFor(cat, color),
            };
            State.wardrobe.push(item);
            persistWardrobe();
            close();
            render();
          }
        }, '加入衣橱')
      )
    )
  );
  root.appendChild(modal);
}

// ========== AI 形象顾问 ==========
function renderStylist(root: HTMLElement): void {
  root.appendChild(el('section', { class: 'mb-6' },
    el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-3' }, 'AI 形象顾问'),
    el('p',  { class: 'text-ink-800/65 max-w-xl' },
      '告诉我场合、心情、或者你纠结的搭配。我会基于经典风格给出具体方案。')
  ));

  if (!State.user && !State.demoMode) {
    root.appendChild(el('div', {
      class: 'mb-6 p-4 rounded-xl bg-accent-soft border border-accent/30 text-sm text-accent-deep flex items-center justify-between'
    },
      el('div', {}, '登录后即可与 AI 顾问对话(免费)。'),
      el('button', { class: 'px-4 py-1.5 rounded-full bg-accent text-white text-xs', onclick: openAccount }, '现在登录')
    ));
  } else if (State.demoMode) {
    root.appendChild(el('div', {
      class: 'mb-6 p-4 rounded-xl bg-ink-100 border border-ink-200 text-sm text-ink-800/70'
    }, '当前是本地演示模式,AI 对话需要部署到 Cloudflare 后才能用真实 API。'));
  }

  const quick = [
    '今天周一,有重要会议,该怎么穿?',
    '我有一件碎花连衣裙,怎么搭不会显土?',
    '夏天约会但又不想太露,有什么思路?',
    '秋冬通勤怎么穿出高级感?',
  ];
  if (State.chat.length === 0) {
    root.appendChild(el('div', { class: 'mb-6' },
      el('div', { class: 'text-xs text-ink-800/55 mb-2' }, '试试问我:'),
      el('div', { class: 'flex flex-wrap gap-2' },
        ...quick.map(q => chip(q, { onclick: () => sendChat(q) }))
      )
    ));
  }

  const stream = el('div', {
    id: 'chatStream',
    class: 'space-y-4 mb-6 max-h-[55vh] overflow-y-auto pr-2'
  });
  State.chat.forEach(m => stream.appendChild(renderMsg(m)));
  root.appendChild(stream);

  const input = el('textarea', {
    class: 'w-full px-4 py-3 rounded-2xl border border-ink-200 bg-white text-sm resize-none focus:outline-none focus:border-ink-800 transition',
    placeholder: '描述你的需求,比如:36 岁通勤,微胖,想穿出气质…',
    rows: 2,
  });
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const v = input.value.trim();
      if (v) { input.value = ''; sendChat(v); }
    }
  });

  root.appendChild(el('div', { class: 'flex gap-2 items-end' },
    el('div', { class: 'flex-1' }, input),
    el('button', {
      class: 'px-5 py-3 rounded-2xl bg-ink-900 text-ink-50 hover:bg-ink-800 text-sm font-medium',
      onclick: () => { const v = input.value.trim(); if (v) { input.value = ''; sendChat(v); } }
    }, '发送'),
    State.chat.length > 0
      ? el('button', {
          class: 'px-3 py-3 rounded-2xl bg-ink-100 hover:bg-ink-200 text-xs',
          onclick: () => { State.chat = []; persistChat(); render(); }
        }, '清空')
      : null
  ));
}

function renderMsg(m: ChatMessage): HTMLElement {
  const wrap = el('div', { class: 'flex ' + (m.role === 'user' ? 'justify-end' : 'justify-start') });
  wrap.appendChild(el('div', { class: 'bubble ' + (m.role === 'user' ? 'bubble-user' : 'bubble-ai') }, m.content));
  return wrap;
}

async function sendChat(text: string): Promise<void> {
  State.chat.push({ role: 'user', content: text });
  persistChat();
  render();

  if (State.demoMode) {
    State.chat.push({
      role: 'assistant',
      content: '(演示模式)部署到 Cloudflare 并配置 ANTHROPIC_API_KEY 后我就能真实回答你了。届时我会基于「穿搭研究所」的标签体系给出 2-3 套具体方案。'
    });
    persistChat(); render(); return;
  }
  if (!State.user) {
    State.chat.push({ role: 'assistant', content: '请先登录,登录后我才能记住我们的对话。' });
    persistChat(); render(); return;
  }

  const stream = $<HTMLElement>('#chatStream');
  const placeholder = el('div', { class: 'flex justify-start', id: 'pending' },
    el('div', { class: 'bubble bubble-ai dots text-ink-800/55' },
      el('span'), el('span'), el('span')
    )
  );
  stream?.appendChild(placeholder);
  stream?.scrollTo({ top: stream.scrollHeight, behavior: 'smooth' });

  try {
    const r = await api<{ reply: string }>('/api/stylist', { method: 'POST', body: { messages: State.chat } });
    State.chat.push({ role: 'assistant', content: r.reply });
    persistChat();
    render();
  } catch (err) {
    State.chat.push({ role: 'assistant', content: '抱歉,出了点问题:' + (err as Error).message });
    persistChat();
    render();
  }
}

// ========== 商城 ==========
function renderShop(root: HTMLElement): void {
  const focusIds = new Set<string>(Store.load<string[]>('shop_focus', []) || []);
  Store.save('shop_focus', null);

  root.appendChild(el('section', { class: 'mb-8' },
    el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-3' }, '同款商城'),
    el('p',  { class: 'text-ink-800/65 max-w-xl' },
      focusIds.size > 0 ? '为你高亮显示了刚才那套搭配的单品。' : '所有单品均已对接合作品牌,价格仅供参考。')
  ));

  const filterBar = el('div', { class: 'mb-6 flex flex-wrap gap-2' });
  const cats = ['全部', ...new Set(ITEMS.map(i => i.cat))];
  const activeCat = Store.load<string>('shop_cat', '全部');
  cats.forEach(c => {
    filterBar.appendChild(chip(c, {
      active: activeCat === c,
      onclick: () => { Store.save('shop_cat', c); render(); }
    }));
  });
  root.appendChild(filterBar);

  const list = activeCat === '全部' ? ITEMS : ITEMS.filter(i => i.cat === activeCat);
  list.sort((a, b) => (focusIds.has(b.id) ? 1 : 0) - (focusIds.has(a.id) ? 1 : 0));

  const grid = el('div', { class: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5' });
  list.forEach(it => {
    grid.appendChild(el('article', {
      class: 'rounded-2xl border bg-white overflow-hidden ' +
             (focusIds.has(it.id) ? 'border-accent shadow-md' : 'border-ink-200')
    },
      el('div', { class: 'aspect-square bg-ink-100 p-4', html: it.render() }),
      el('div', { class: 'p-4' },
        el('div', { class: 'text-xs text-ink-800/55' }, it.cat + ' · ' + (it.style || []).slice(0, 2).join('/')),
        el('div', { class: 'font-medium mt-0.5' }, it.name),
        el('div', { class: 'flex items-center justify-between mt-3' },
          el('span', { class: 'text-accent-deep font-semibold' }, '¥' + it.price),
          el('button', {
            class: 'px-3 py-1.5 text-xs rounded-full bg-ink-900 text-ink-50 hover:bg-ink-800',
            onclick: () => addToCart(it.id, 1)
          }, '加入购物车')
        )
      )
    ));
  });
  root.appendChild(grid);
}

// ========== 购物车 ==========
function renderCart(root: HTMLElement): void {
  root.appendChild(el('h1', { class: 'font-display text-4xl md:text-5xl tracking-tight mb-6' }, '购物车'));

  if (!State.user && !State.demoMode) {
    root.appendChild(el('div', { class: 'empty' },
      el('div', { class: 'mb-3' }, '请先登录,购物车与你的账号绑定'),
      el('button', { class: 'px-5 py-2.5 rounded-full bg-ink-900 text-ink-50 text-sm', onclick: openAccount }, '登录 / 注册')
    ));
    return;
  }

  if (State.cart.length === 0) {
    root.appendChild(el('div', { class: 'empty' },
      el('div', { class: 'mb-3' }, '购物车空空如也'),
      el('button', { class: 'px-5 py-2.5 rounded-full bg-ink-900 text-ink-50 text-sm', onclick: () => go('shop') }, '去逛商城')
    ));
    return;
  }

  const list = el('div', { class: 'space-y-3 mb-6' });
  State.cart.forEach(c => {
    const it = getItem(c.item_id);
    if (!it) return;
    list.appendChild(el('article', { class: 'flex items-center gap-4 p-4 rounded-2xl border border-ink-200 bg-white' },
      el('div', { class: 'w-20 h-20 bg-ink-100 rounded-xl p-2 shrink-0', html: it.render() }),
      el('div', { class: 'flex-1 min-w-0' },
        el('div', { class: 'text-xs text-ink-800/55' }, it.cat),
        el('div', { class: 'font-medium truncate' }, it.name),
        el('div', { class: 'text-accent-deep font-semibold mt-1' }, '¥' + it.price)
      ),
      el('div', { class: 'flex items-center gap-2 shrink-0' },
        el('button', {
          class: 'w-8 h-8 rounded-full bg-ink-100 hover:bg-ink-200 text-lg',
          onclick: () => setCartQty(c.item_id, c.qty - 1)
        }, '−'),
        el('span', { class: 'w-8 text-center font-medium' }, String(c.qty)),
        el('button', {
          class: 'w-8 h-8 rounded-full bg-ink-100 hover:bg-ink-200 text-lg',
          onclick: () => setCartQty(c.item_id, c.qty + 1)
        }, '+'),
        el('button', {
          class: 'ml-2 text-xs text-ink-800/50 hover:text-accent',
          onclick: () => removeFromCart(c.item_id)
        }, '移除')
      )
    ));
  });
  root.appendChild(list);

  const total = totalCartPrice();
  root.appendChild(el('div', {
    class: 'sticky bottom-20 md:bottom-4 bg-ink-900 text-ink-50 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3'
  },
    el('div', {},
      el('div', { class: 'text-xs text-ink-50/60' }, totalCartCount() + ' 件商品'),
      el('div', { class: 'text-2xl font-display font-semibold' }, '合计 ¥' + total)
    ),
    el('div', { class: 'flex gap-2' },
      el('button', {
        class: 'px-4 py-2.5 rounded-full bg-ink-50/10 hover:bg-ink-50/20 text-sm',
        onclick: async () => {
          if (!confirm('清空购物车?')) return;
          if (State.demoMode || !State.user) {
            State.cart = []; persistDemoCart(); render();
          } else {
            try {
              await api('/api/cart', { method: 'DELETE' });
              State.cart = []; render();
            } catch (e) { toast((e as Error).message, 'error'); }
          }
        }
      }, '清空'),
      el('button', {
        class: 'px-6 py-2.5 rounded-full bg-accent hover:bg-accent-deep text-sm font-semibold',
        onclick: checkout
      }, '去结算 →')
    )
  ));
}

// ========== 账户 ==========
function openAccount(): void {
  const root = $<HTMLElement>('#modalRoot');
  if (!root) return;
  const close = () => { root.innerHTML = ''; };

  const panel = el('div', { class: 'modal-panel p-6 md:p-8 max-w-md' });

  if (State.user) renderAccountLoggedIn(panel, close);
  else renderAuthForm(panel, close);

  root.appendChild(el('div', {
    class: 'modal-bg',
    onclick: (e: MouseEvent) => { if ((e.target as HTMLElement).classList.contains('modal-bg')) close(); }
  }, panel));
}

function renderAuthForm(panel: HTMLElement, close: () => void): void {
  let mode: 'login' | 'register' = 'login';

  const emailInput = el('input', {
    type: 'email', class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm', placeholder: '你的邮箱'
  });
  const pwInput = el('input', {
    type: 'password', class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm', placeholder: '密码(至少 6 位)'
  });
  const nameInput = el('input', {
    type: 'text', class: 'w-full px-3 py-2 border border-ink-200 rounded-lg bg-white text-sm', placeholder: '昵称(可选)'
  });
  const errBox = el('div', { class: 'text-xs text-accent-deep' });

  const submitBtn = el('button', {
    class: 'w-full px-5 py-2.5 rounded-full bg-ink-900 text-ink-50 hover:bg-ink-800 text-sm font-medium',
    onclick: async () => {
      errBox.textContent = '';
      const email = emailInput.value.trim();
      const password = pwInput.value;
      if (State.demoMode) { errBox.textContent = '本地演示模式不能真实注册/登录,请部署后再试'; return; }
      try {
        if (mode === 'login') {
          const r = await api<{ user: User }>('/api/auth/login', { method: 'POST', body: { email, password } });
          State.user = r.user;
        } else {
          const r = await api<{ user: User }>('/api/auth/register', {
            method: 'POST', body: { email, password, name: nameInput.value.trim() }
          });
          State.user = r.user;
        }
        try {
          const c = await api<{ items: CartLine[] }>('/api/cart');
          State.cart = c.items || [];
        } catch { /* ignore */ }
        close();
        render();
        toast('欢迎,' + (State.user.name || State.user.email));
      } catch (e) {
        errBox.textContent = (e as Error).message;
      }
    }
  }, mode === 'login' ? '登录' : '注册');

  function rebuild(): void {
    panel.innerHTML = '';
    const isReg = mode === 'register';
    panel.appendChild(el('div', { class: 'flex items-start justify-between mb-6' },
      el('h2', { class: 'font-display text-2xl font-semibold' }, isReg ? '创建账号' : '登录'),
      el('button', { class: 'text-2xl text-ink-800/50', onclick: close }, '×')
    ));
    panel.appendChild(el('div', { class: 'space-y-3' },
      el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '邮箱'), emailInput),
      el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '密码'), pwInput),
      isReg ? el('div', {}, el('label', { class: 'text-xs text-ink-800/65 block mb-1' }, '昵称'), nameInput) : null,
      errBox
    ));
    panel.appendChild(el('div', { class: 'mt-6 space-y-3' },
      submitBtn,
      el('button', {
        class: 'w-full text-xs text-ink-800/65 hover:text-ink-900',
        onclick: () => {
          mode = isReg ? 'login' : 'register';
          submitBtn.textContent = mode === 'login' ? '登录' : '注册';
          rebuild();
        }
      }, isReg ? '已经有账号?去登录' : '还没账号?去注册')
    ));
  }
  rebuild();
}

function renderAccountLoggedIn(panel: HTMLElement, close: () => void): void {
  if (!State.user) return;
  panel.innerHTML = '';
  panel.appendChild(el('div', { class: 'flex items-start justify-between mb-6' },
    el('div', {},
      el('h2', { class: 'font-display text-2xl font-semibold' }, '你好,' + (State.user.name || '朋友')),
      el('div', { class: 'text-xs text-ink-800/55 mt-1' }, State.user.email)
    ),
    el('button', { class: 'text-2xl text-ink-800/50', onclick: close }, '×')
  ));

  const ordersBox = el('div', { class: 'space-y-2 max-h-72 overflow-auto' },
    el('div', { class: 'text-xs text-ink-800/55' }, '加载订单中…')
  );
  panel.appendChild(el('h3', { class: 'font-display text-lg font-semibold mb-3' }, '订单历史'));
  panel.appendChild(ordersBox);

  api<{ orders: Order[] }>('/api/checkout').then(r => {
    ordersBox.innerHTML = '';
    if (!r.orders || r.orders.length === 0) {
      ordersBox.appendChild(el('div', { class: 'text-sm text-ink-800/50' }, '还没有订单'));
      return;
    }
    r.orders.forEach(o => {
      ordersBox.appendChild(el('div', { class: 'p-3 border border-ink-200 rounded-xl text-sm' },
        el('div', { class: 'flex items-center justify-between' },
          el('span', { class: 'font-medium' }, '订单 #' + o.id),
          el('span', { class: 'text-accent-deep font-semibold' }, '¥' + o.total)
        ),
        el('div', { class: 'text-xs text-ink-800/55 mt-1' },
          (o.items || []).map(i => i.name + ' ×' + i.qty).join(' · '))
      ));
    });
  }).catch((e: Error) => {
    ordersBox.innerHTML = '';
    ordersBox.textContent = '加载订单失败:' + e.message;
  });

  panel.appendChild(el('div', { class: 'mt-6 pt-6 border-t border-ink-200 flex justify-end' },
    el('button', {
      class: 'px-5 py-2.5 rounded-full text-sm bg-ink-100 hover:bg-ink-200',
      onclick: async () => {
        try { await api('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
        State.user = null;
        State.cart = [];
        close();
        render();
        toast('已退出登录');
      }
    }, '退出登录')
  ));
}
